import { prevPeriod, isMultiMonth } from "@/lib/prevPeriod";
import { useSharedKpiFilters } from "@/lib/kpiFilterMemory";
import { CreateReportButton } from "@/components/CreateReportButton";
import { PageActions } from "@/components/PageActions";
import { LastImportInfo } from "@/components/LastImportInfo";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toParams } from "@/lib/kpi";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend,
  CartesianGrid, PieChart, Pie, Cell, ComposedChart, ReferenceLine
} from "recharts";
import {
  Database, Percent, ShoppingCart, Users, TrendingUp, Wallet, Download, Upload, Settings, Search, X as XIcon, Trophy, AlertTriangle,
  ChevronDown, LayoutGrid, Target, Layers,
} from "lucide-react";
import { toast } from "sonner";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";
import EbeSimulator from "@/pages/wasaplan/EbeSimulator";
import { useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Table2, Calculator, FileText } from "lucide-react";
import CustomDates from "@/components/filters/CustomDates";

import { OrbitAI } from "@/components/OrbitAI";
import { CommissionsTable, RatioEvolution } from "@/pages/wasaplan/SyntheseWidgets";
const normMois = (m) => (m || "").toString().normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toUpperCase().trim();

/* ─── constants ──────────────────────────────────────────────────────── */
const MONTHS_FR = ["JANVIER", "FEVRIER", "MARS", "AVRIL", "MAI", "JUIN", "JUILLET", "AOUT", "SEPTEMBRE", "OCTOBRE", "NOVEMBRE", "DECEMBRE"];
const MONTH_ABBR = { JANVIER: "Jan", FEVRIER: "Fév", MARS: "Mar", AVRIL: "Avr", MAI: "Mai", JUIN: "Juin", JUILLET: "Juil", AOUT: "Août", SEPTEMBRE: "Sep", OCTOBRE: "Oct", NOVEMBRE: "Nov", DECEMBRE: "Déc" };
const CLR = { blue: "#2563EB", green: "#10B981", orange: "#F59E0B", rose: "#F43F5E", violet: "#8B5CF6", yellow: "#EAB308", muted: "#94A3B8" };
const ENSEIGNE_COLORS = { CARREFOUR: "#2563EB", LECLERC: "#F59E0B", "SUPER U": "#8B5CF6", U: "#EC4899", INTERMARCHE: "#10B981", AUCHAN: "#EF4444", MONOPRIX: "#0EA5E9" };

/* ─── formatters ─────────────────────────────────────────────────────── */
const fmtEur = v => v == null || isNaN(v) ? "—" : Math.round(v).toLocaleString("fr-FR").replace(/\u202F/g, " ").replace(/,/g, " ") + " €";
const fmtEurShort = v => {
  if (v == null || isNaN(v)) return "—";
  return Math.round(v).toLocaleString("fr-FR").replace(/\u202F/g, " ").replace(/,/g, " ") + " €";
};
const fmtPct = (v, d = 1) => v == null || isNaN(v) ? "—" : `${(v * 100).toFixed(d).replace(".", ",")}%`;
const fmtNum = (v, d = 0) => v == null || isNaN(v) ? "—" : v.toLocaleString("fr-FR", { maximumFractionDigits: d, minimumFractionDigits: d });

/* ─── Multi-select (compact) ─────────────────────────────────────────── */
function MultiSelect({ label, options, values, onChange, testid, minWidth = "min-w-[140px]" }) {
  return <FilterMultiSelect label={label} options={options} value={values || []} onChange={onChange} testid={testid} minWidth={minWidth} size="sm" placeholder={`Tous les ${label}`}/>;
}

/* ─── KPI Card (compact avec variation) ──────────────────────────────── */
function KpiCard({ icon: Icon, iconColor = "text-blue-600", iconBg = "bg-blue-50", label, value, delta, unit = "pt", testid, deltaLabel = "vs mois préc." }) {
  const up = delta != null && delta > 0;
  const down = delta != null && delta < 0;
  const tone = up ? "text-emerald-600" : down ? "text-rose-600" : "text-slate-500";
  const sign = up ? "↗" : down ? "↘" : "→";
  return (
    <div className="bg-white rounded-xl border border-slate-200 px-3 py-2.5 shadow-[0_1px_3px_rgba(15,23,42,0.04)] min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider truncate">{label}</span>
        {Icon && <div className={`w-6 h-6 rounded-md ${iconBg} grid place-items-center shrink-0`}><Icon className={`w-3 h-3 ${iconColor}`}/></div>}
      </div>
      <div className="font-bold text-slate-900 text-[17px] tabular-nums leading-tight truncate">{value}</div>
      {delta != null && (
        <div className={`text-[10px] mt-1 font-semibold ${tone} tabular-nums flex items-center gap-1`}>
          <span>{sign}</span>
          <span>{delta > 0 ? "+" : ""}{Math.abs(delta) < 10 ? delta.toFixed(1).replace(".", ",") : Math.round(delta)}{unit === "pt" ? " pt" : unit === "%" ? "%" : ""}</span>
          <span className="text-slate-400 font-normal">{deltaLabel}</span>
        </div>
      )}
    </div>
  );
}

/* ─── FilterBar (DR / Secteur / Corners / Exercice / Mois) ─────────── */
function FilterBar({ filters, f, setF, exercicesFromData }) {
  const drs = filters?.drs || [];
  const secteurs = filters?.secteurs || [];
  // Corner list cascades on DR + Secteur selection (client-side)
  const corners = (filters?.corners || [])
    .filter(c => !f.drs?.length || f.drs.includes(c.dr))
    .filter(c => !f.secteurs?.length || f.secteurs.includes(c.secteur))
    .map(c => c.corner)
    .filter(Boolean);
  // Deduplicate while preserving order
  const cornerOpts = Array.from(new Set(corners));
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-2" data-testid="wp-filter-bar">
      <div className="flex flex-col">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">DR</span>
        <MultiSelect label="DR" testid="wp-flt-dr" options={drs} values={f.drs}
                     onChange={v => setF(o => ({
                       ...o, drs: v,
                       // Purge secteurs/corners qui n'appartiennent plus à la sélection DR
                       secteurs: (o.secteurs || []).filter(s => {
                         if (!v.length) return true;
                         return (filters?.corners || []).some(c => c.secteur === s && v.includes(c.dr));
                       }),
                       corners: (o.corners || []).filter(cn => {
                         if (!v.length) return true;
                         return (filters?.corners || []).some(c => c.corner === cn && v.includes(c.dr));
                       }),
                     }))}/>
      </div>
      <div className="flex flex-col">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Secteur</span>
        <MultiSelect label="Secteur" testid="wp-flt-secteur" options={secteurs} values={f.secteurs || []}
                     onChange={v => setF(o => ({
                       ...o, secteurs: v,
                       corners: (o.corners || []).filter(cn => {
                         if (!v.length) return true;
                         return (filters?.corners || []).some(c => c.corner === cn && v.includes(c.secteur));
                       }),
                     }))}/>
      </div>
      <div className="flex flex-col flex-1 min-w-[200px]">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Corners</span>
        <MultiSelect label="corners" testid="wp-flt-corner" options={cornerOpts} values={f.corners} onChange={v => setF(o => ({ ...o, corners: v }))} minWidth="min-w-full"/>
      </div>
      <div className="flex flex-col">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Année</span>
        <FilterMultiSelect testid="wp-flt-annee" label="Année" placeholder="Toutes" minWidth={100} menuWidth={160}
          options={(filters?.annees || []).map(String)}
          value={(f.annees || []).map(String)}
          onChange={arr => setF(o => ({ ...o, annees: arr.map(Number) }))}/>
      </div>
      <div className="flex flex-col">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Exercice</span>
        <FilterMultiSelect testid="wp-flt-exercice" label="Exercice" placeholder="Tous" minWidth={110} menuWidth={180}
          options={exercicesFromData}
          value={(f.exercice || "").split(",").filter(Boolean)}
          onChange={arr => setF(o => ({ ...o, exercice: arr.length ? arr.join(",") : null }))}/>
      </div>
      <div className="flex flex-col">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Mois</span>
        <FilterMultiSelect testid="wp-flt-mois" label="Mois" placeholder="Tous" minWidth={120} menuWidth={200}
          options={MONTHS_FR.map(m => ({ value: m, label: m.charAt(0) + m.slice(1).toLowerCase() }))}
          value={f.mois || []}
          onChange={arr => setF(o => ({ ...o, mois: arr }))}/>
        <CustomDates testid="wp-flt-dates" value={f} onChange={p => setF(o => ({ ...o, ...p }))} />
      </div>
      {(f.drs.length || (f.secteurs || []).length || f.corners.length || f.exercice || f.mois.length || (f.annees || []).length || (f.date_from || f.date_to ? 1 : 0)) > 0 && (
        <button onClick={() => setF({ drs: [], secteurs: [], corners: [], exercice: null, mois: [], annees: [] })}
                data-testid="wp-flt-reset"
                className="ml-auto self-end text-[11px] text-rose-600 font-semibold hover:underline flex items-center gap-1 mb-1">
          <XIcon className="w-3 h-3"/> Réinitialiser
        </button>
      )}
    </div>
  );
}

/* ─── Helper: aggregate rows into monthly summary ───────────────────── */
// Clé de regroupement d'une ligne sur une dimension (region/enseigne/secteur/dr/corner). Les lignes dont la colonne est
// absente du fichier importé vont dans « Non renseigné » au lieu d'être écartées → tous les widgets couvrent les mêmes lignes.
const NON_RENSEIGNE = "Non renseigné";
const dimKey = (r, dim) => ((r[dim] ?? "").toString().trim()) || NON_RENSEIGNE;
const CIBLE_KEYS = ["cible_ratio_achat", "cible_taux_mb", "cible_taux_casse", "cible_taux_ms", "cible_prod", "cible_taux_other", "cible_taux_mn"];
function aggregate(rows) {
  const s = rows.reduce((a, r) => {
    a.gross_sales += r.gross_sales || 0;
    a.sales_ttc += r.sales_ttc || 0;
    a.sales_n1 += r.sales_n1 || 0;
    a.sales_commissions += r.sales_commissions || 0;
    a.food_costs += Math.abs(r.food_costs || 0);
    a.marge_brute += r.marge_brute || 0;
    a.labor_costs += Math.abs(r.labor_costs || 0);
    a.other_costs += Math.abs(r.other_costs || 0);
    a.contribution += r.contribution || 0;
    a.heures += r.heures || 0;
    a.production_reel += r.production_reel || 0;
    if (r.cible_taux_mn != null && !isNaN(r.cible_taux_mn)) { a._cible_sum += Math.abs(r.cible_taux_mn); a._cible_n += 1; }
    for (const k of CIBLE_KEYS) { const v = r[k]; if (v != null && !isNaN(v)) { a._c[k].sum += Math.abs(v); a._c[k].n += 1; } }
    a.n += 1;
    return a;
  }, { gross_sales: 0, sales_ttc: 0, sales_n1: 0, sales_commissions: 0, food_costs: 0, marge_brute: 0, labor_costs: 0, other_costs: 0, contribution: 0, heures: 0, production_reel: 0, n: 0, _cible_sum: 0, _cible_n: 0,
       _c: Object.fromEntries(CIBLE_KEYS.map(k => [k, { sum: 0, n: 0 }])) });
  const ks = new Set(rows.map(r => ckSlug(r.corner_key || r.corner)));
  const singleCk = ks.size === 1 ? [...ks][0] : null;
  const cibleOf = (key) => { const m = manualTarget(key, singleCk); if (m != null) return m; const c = s._c[key]; return c.n ? c.sum / c.n : null; };
  return {
    ...s,
    cible_taux_mn: cibleOf("cible_taux_mn"),
    cible_ratio_achat: cibleOf("cible_ratio_achat"), cible_taux_mb: cibleOf("cible_taux_mb"), cible_taux_casse: cibleOf("cible_taux_casse"),
    cible_taux_ms: cibleOf("cible_taux_ms"), cible_prod: cibleOf("cible_prod"), cible_taux_other: cibleOf("cible_taux_other"),
    tva: s.sales_ttc - s.gross_sales,
    sales_ht_net_com: s.gross_sales - s.sales_commissions,
    taux_commissions: s.gross_sales ? s.sales_commissions / s.gross_sales : 0,
    ratio_foodcost: s.gross_sales ? s.food_costs / s.gross_sales : 0,
    taux_mb: s.gross_sales ? s.marge_brute / s.gross_sales : 0,
    taux_ms: s.gross_sales ? s.labor_costs / s.gross_sales : 0,
    taux_other: s.gross_sales ? s.other_costs / s.gross_sales : 0,
    taux_mn: s.gross_sales ? s.contribution / s.gross_sales : 0,
    productivite: s.heures ? s.production_reel / s.heures : 0,
    lfl: s.sales_n1 ? (s.gross_sales - s.sales_n1) / s.sales_n1 : 0,
  };
}

function YearComparison({ rows, years }) {
  const [y1, y2] = years;
  const data = useMemo(() => {
    const by = (y) => aggregate(rows.filter(r => Number(r.annee) === y));
    const a1 = by(y1), a2 = by(y2);
    const months = MONTHS_FR.filter(m => rows.some(r => r.mois === m));
    const monthly = months.map(m => {
      const m1 = aggregate(rows.filter(r => Number(r.annee) === y1 && r.mois === m)), m2 = aggregate(rows.filter(r => Number(r.annee) === y2 && r.mois === m));
      return { mois: m === "JUILLET" ? "Juil" : m === "JUIN" ? "Juin" : m.charAt(0) + m.slice(1, 3).toLowerCase(), [`CA ${y1}`]: Math.round(m1.gross_sales), [`CA ${y2}`]: Math.round(m2.gross_sales), [`EBE ${y1}`]: Math.round(m1.contribution), [`EBE ${y2}`]: Math.round(m2.contribution) };
    });
    return { a1, a2, monthly };
  }, [rows, y1, y2]);
  const { a1, a2, monthly } = data;
  const evo = (v1, v2) => v1 ? (v2 - v1) / Math.abs(v1) : null;
  const lines = [
    { key: "ca", label: "CA HT", v1: a1.gross_sales, v2: a2.gross_sales, kind: "eur" },
    { key: "mb", label: "Marge brute", v1: a1.marge_brute, v2: a2.marge_brute, kind: "eur", r1: a1.taux_mb, r2: a2.taux_mb },
    { key: "ms", label: "Masse salariale", v1: a1.labor_costs, v2: a2.labor_costs, kind: "eur", r1: a1.taux_ms, r2: a2.taux_ms, inverse: true },
    { key: "ebe", label: "EBE (résultat d'exploitation)", v1: a1.contribution, v2: a2.contribution, kind: "eur", r1: a1.taux_mn, r2: a2.taux_mn, bold: true },
  ];
  const Delta = ({ v, inverse, unit }) => {
    if (v == null || isNaN(v)) return <span className="text-slate-400">—</span>;
    const good = inverse ? v <= 0 : v >= 0;
    return <span className={`font-bold tabular-nums ${good ? "text-emerald-600" : "text-rose-600"}`}>{v >= 0 ? "+" : ""}{unit === "pt" ? `${(v * 100).toFixed(1).replace(".", ",")} pt` : fmtPct(v)}</span>;
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)]" data-testid="wp-year-comparison">
      <div className="flex items-center justify-between mb-3">
        <div><h3 className="text-[13px] font-bold text-slate-900">Comparaison annuelle {y1} vs {y2}</h3><p className="text-[11px] text-slate-500">Cumul des mois sélectionnés · CA HT, marge, masse salariale, EBE</p></div>
        <div className="flex gap-2 text-[10px]"><span className="inline-flex items-center gap-1 text-slate-500"><span className="w-2.5 h-2.5 rounded-sm bg-slate-300"/>{y1}</span><span className="inline-flex items-center gap-1 text-slate-500"><span className="w-2.5 h-2.5 rounded-sm bg-[#0b1f4d]"/>{y2}</span></div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-4">
        <table className="w-full text-[12px]">
          <thead><tr className="text-[9px] uppercase tracking-wider text-slate-400"><th className="text-left py-1.5">Indicateur</th><th className="text-right py-1.5">{y1}</th><th className="text-right py-1.5">{y2}</th><th className="text-right py-1.5">Évol.</th><th className="text-right py-1.5">Taux {y1} → {y2}</th></tr></thead>
          <tbody>
            {lines.map(l => (
              <tr key={l.key} data-testid={`wp-yc-${l.key}`} className={`border-t border-slate-100 ${l.bold ? "font-bold bg-slate-50/60" : ""}`}>
                <td className="py-2 text-slate-800">{l.label}</td>
                <td className="py-2 text-right tabular-nums text-slate-500">{fmtEur(l.v1)}</td>
                <td className="py-2 text-right tabular-nums text-slate-900">{fmtEur(l.v2)}</td>
                <td className="py-2 text-right"><Delta v={evo(l.v1, l.v2)} inverse={l.inverse}/></td>
                <td className="py-2 text-right tabular-nums text-slate-600">{l.r1 != null ? <>{fmtPct(l.r1)} → <b>{fmtPct(l.r2)}</b> <Delta v={l.r2 - l.r1} inverse={l.inverse} unit="pt"/></> : "—"}</td>
              </tr>))}
          </tbody>
        </table>
        <div className="h-[200px]">
          <ResponsiveContainer>
            <ComposedChart data={monthly} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false}/>
              <XAxis dataKey="mois" tick={{ fontSize: 10 }}/><YAxis tick={{ fontSize: 9 }} width={44} tickFormatter={fmtEurShort}/>
              <Tooltip formatter={v => fmtEur(v)}/><Legend wrapperStyle={{ fontSize: 10 }}/>
              <Bar dataKey={`CA ${y1}`} fill="#cbd5e1" radius={[4, 4, 0, 0]}/><Bar dataKey={`CA ${y2}`} fill="#0b1f4d" radius={[4, 4, 0, 0]}/>
              <Line type="monotone" dataKey={`EBE ${y1}`} stroke="#94a3b8" strokeDasharray="4 3" dot={false} strokeWidth={2}/><Line type="monotone" dataKey={`EBE ${y2}`} stroke="#f97316" dot={false} strokeWidth={2}/>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}


/* ─── Cibles Panel (right drawer) ────────────────────────────────────── */
const TARGET_DEFS = [
  { block: "Achats & Marchandises", color: "bg-emerald-50 border-emerald-200 text-emerald-800", items: [
    { key: "cible_ratio_achat", label: "Cible Ratio d'achat (Foodcost)", kind: "pct" },
    { key: "cible_taux_mb", label: "Cible Taux Marge Brute", kind: "pct" },
    { key: "cible_taux_casse", label: "Cible Taux de Casse", kind: "pct" },
  ]},
  { block: "Masse Salariale", color: "bg-rose-50 border-rose-200 text-rose-800", items: [
    { key: "cible_taux_ms", label: "Cible Taux MS", kind: "pct" },
    { key: "cible_prod", label: "Cible Productivité (€/h)", kind: "eurh" },
  ]},
  { block: "Autres charges", color: "bg-violet-50 border-violet-200 text-violet-800", items: [
    { key: "cible_taux_other", label: "Cible Taux Autres charges", kind: "pct" },
  ]},
  { block: "Résultats", color: "bg-amber-50 border-amber-200 text-amber-800", items: [
    { key: "cible_taux_mn", label: "Cible Marge Nette", kind: "pct" },
  ]},
];
// Cibles manuelles actives { targets: réseau, corners: {corner_key: {...}} } — lues par aggregate() pour la cible marge nette
let MANUAL_TARGETS = { targets: {}, corners: {} };
const ckSlug = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
const manualTarget = (key, cornerKey) => {
  const c = cornerKey ? MANUAL_TARGETS.corners?.[ckSlug(cornerKey)]?.[key] : undefined;
  return c != null ? c : (MANUAL_TARGETS.targets?.[key] ?? null);
};
const fmtTarget = (v, kind) => v == null ? "—" : kind === "pct" ? fmtPct(v) : fmtNum(v, 1) + " €/h";
const toInput = (v, kind) => v == null ? "" : kind === "pct" ? String(Math.round(v * 1000) / 10).replace(".", ",") : String(Math.round(v * 10) / 10).replace(".", ",");
const fromInput = (s, kind) => { const n = parseFloat(String(s).replace(",", ".").replace(/\s/g, "")); return isNaN(n) ? null : kind === "pct" ? n / 100 : n; };

function CiblesPanel({ rows, onClose, manual, onSaveTargets, canEdit }) {
  const cornerOpts = useMemo(() => {
    const m = new Map();
    rows.forEach(r => { const k = ckSlug(r.corner_key || r.corner); if (k && !m.has(k)) m.set(k, r.corner || r.corner_key); });
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);
  const [scope, setScope] = useState("");  // "" = réseau, sinon corner_key
  const scopedRows = scope ? rows.filter(r => ckSlug(r.corner_key || r.corner) === scope) : rows;
  const avg = k => {
    const arr = scopedRows.map(r => r[k]).filter(v => v != null && !isNaN(v));
    return arr.length ? arr.reduce((s, v) => s + Math.abs(v), 0) / arr.length : null;
  };
  const current = scope ? (manual.corners?.[scope] || {}) : (manual.targets || {});
  const items = TARGET_DEFS.flatMap(b => b.items);
  const [draft, setDraft] = useState({});
  useEffect(() => { setDraft(Object.fromEntries(items.map(it => [it.key, toInput(current[it.key], it.kind)]))); }, [scope, manual]); // eslint-disable-line react-hooks/exhaustive-deps
  const [saving, setSaving] = useState(false);
  const dirty = items.some(it => fromInput(draft[it.key] ?? "", it.kind) !== (current[it.key] ?? null));
  const save = async () => {
    setSaving(true);
    const payload = Object.fromEntries(items.map(it => [it.key, (draft[it.key] ?? "").trim() === "" ? null : fromInput(draft[it.key], it.kind)]));
    try { await onSaveTargets(payload, scope || null); toast.success(scope ? "Cibles du magasin enregistrées" : "Cibles réseau enregistrées"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const nCorners = Object.keys(manual.corners || {}).length;
  return (
    <div className="fixed inset-0 z-[1000] flex justify-end" data-testid="wp-cibles-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <div className="relative bg-white w-full max-w-md h-full shadow-2xl overflow-y-auto flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-600">Cibles Anaplan</div>
            <h2 className="font-display font-bold text-slate-900">Objectifs de chaque indicateur</h2>
            <p className="text-[11px] text-slate-500">Priorité : magasin → réseau → moyenne du fichier ({rows.length} lignes){nCorners ? ` · ${nCorners} magasin(s) personnalisé(s)` : ""}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="wp-cibles-close"><XIcon className="w-4 h-4"/></button>
        </div>
        <div className="px-4 pt-3">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Périmètre</label>
          <select value={scope} onChange={e => setScope(e.target.value)} className="mt-1 w-full h-8 rounded-md border border-slate-300 bg-white px-2 text-xs" data-testid="wp-cibles-scope">
            <option value="">Réseau (toutes les cibles)</option>
            {cornerOpts.map(([k, label]) => <option key={k} value={k}>{label}{manual.corners?.[k] ? " · personnalisé" : ""}</option>)}
          </select>
        </div>
        <div className="p-4 space-y-4 flex-1">
          {TARGET_DEFS.map(c => (
            <div key={c.block} className={`rounded-lg border ${c.color} p-3`}>
              <div className="font-bold text-xs uppercase tracking-wider mb-2">{c.block}</div>
              <div className="space-y-2">
                {c.items.map(it => {
                  const base = avg(it.key); const own = current[it.key];
                  const inherited = scope && own == null ? manual.targets?.[it.key] : undefined;
                  return (
                    <div key={it.key} className="flex items-center justify-between gap-3 text-xs">
                      <div className="min-w-0">
                        <div className="text-slate-700">{it.label}</div>
                        <div className="text-[10px] text-slate-500">Fichier : {fmtTarget(base, it.kind)}
                          {own != null && <span className="ml-1 text-amber-700 font-semibold">· manuel</span>}
                          {inherited != null && <span className="ml-1 text-slate-600">· réseau {fmtTarget(inherited, it.kind)}</span>}
                        </div>
                      </div>
                      {canEdit ? (
                        <div className="flex items-center gap-1 shrink-0">
                          <input value={draft[it.key] ?? ""} onChange={e => setDraft(d => ({ ...d, [it.key]: e.target.value }))} placeholder={toInput(inherited ?? base, it.kind) || "—"} inputMode="decimal"
                                 className="w-20 h-7 rounded border border-slate-300 bg-white px-2 text-right text-xs font-bold tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid={`wp-cible-input-${it.key}`}/>
                          <span className="text-[10px] text-slate-500 w-7">{it.kind === "pct" ? "%" : "€/h"}</span>
                          {(draft[it.key] ?? "") !== "" && <button onClick={() => setDraft(d => ({ ...d, [it.key]: "" }))} title="Revenir à la valeur héritée" className="p-0.5 rounded hover:bg-white/70 text-slate-400" data-testid={`wp-cible-reset-${it.key}`}><XIcon className="w-3 h-3"/></button>}
                        </div>
                      ) : <span className="font-bold text-slate-900 tabular-nums" data-testid={`wp-cible-value-${it.key}`}>{fmtTarget(own ?? inherited ?? base, it.kind)}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        {canEdit && (
          <div className="p-4 pr-24 border-t border-slate-200 flex items-center justify-between gap-2 sticky bottom-0 bg-white">
            <span className="text-[10px] text-slate-500">Champ vide = valeur héritée (réseau puis fichier). La cible Marge Nette pilote le statut « En avance / En retard ».</span>
            <button onClick={save} disabled={!dirty || saving} className="h-8 px-3 rounded-md bg-slate-900 text-white text-xs font-semibold disabled:opacity-40 hover:bg-slate-800" data-testid="wp-cibles-save">{saving ? "Enregistrement…" : "Enregistrer"}</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* Statut vs cible marge nette : ±1 pt autour de la cible */
const CIBLE_TOL = 0.01;
function cibleStatus(agg) {
  if (agg.cible_taux_mn == null || !agg.gross_sales) return null;
  const e = agg.taux_mn - agg.cible_taux_mn;
  if (e >= CIBLE_TOL) return "ahead";
  if (e <= -CIBLE_TOL) return "behind";
  return "ontarget";
}
const CIBLE_STATUS = {
  ahead:    { label: "En avance",        cls: "bg-emerald-100 text-emerald-800", order: 0 },
  ontarget: { label: "Dans l'objectif",  cls: "bg-blue-100 text-blue-800",       order: 1 },
  behind:   { label: "En retard",        cls: "bg-rose-100 text-rose-800",       order: 2 },
};
const SORT_MODES = [
  ["ca",     "CA HT (défaut)"],
  ["best",   "Meilleur → moins bon (MN)"],
  ["worst",  "Moins bon → meilleur (MN)"],
  ["cible",  "Vs cible (avance → retard)"],
];

/* ─── Vue Détaillée : P&L Grid (magasins en colonnes) ────────────────── */
function DetailedGrid({ rows, monthLabel, onOpenCibles, years }) {
  const [cmpGroup, setCmpGroup] = useState(null);
  const canCompare = (years || []).length === 2;
  const [unit, setUnit] = useState("eur"); // eur | pct
  const [showCibles, setShowCibles] = useState(true); // écarts vs cible sous chaque taux
  const [groupBy, setGroupBy] = useState("corner"); // corner | secteur | dr | enseigne
  const [visibleCount, setVisibleCount] = useState(10);
  const [sortMode, setSortMode] = useState("ca");
  const [statusFilter, setStatusFilter] = useState("all"); // all | ahead | ontarget | behind

  // Group rows by chosen dimension
  const grouped = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      const key = r[groupBy] || "—";
      if (!map[key]) map[key] = { key, label: key, dr: r.dr, secteur: r.secteur, enseigne: r.enseigne, region: r.region, rows: [] };
      map[key].rows.push(r);
    });
    let arr = Object.values(map).map(g => { const agg = aggregate(g.rows); return { ...g, agg, status: cibleStatus(agg) }; });
    if (statusFilter !== "all") arr = arr.filter(g => g.status === statusFilter);
    const byMn = (a, b) => (b.agg.taux_mn || 0) - (a.agg.taux_mn || 0);
    if (sortMode === "best") arr.sort(byMn);
    else if (sortMode === "worst") arr.sort((a, b) => -byMn(a, b));
    else if (sortMode === "cible") arr.sort((a, b) => {
      const oa = a.status ? CIBLE_STATUS[a.status].order : 3, ob = b.status ? CIBLE_STATUS[b.status].order : 3;
      if (oa !== ob) return oa - ob;
      const ea = (a.agg.taux_mn || 0) - (a.agg.cible_taux_mn || 0), eb = (b.agg.taux_mn || 0) - (b.agg.cible_taux_mn || 0);
      return eb - ea;
    });
    else arr.sort((a, b) => (b.agg.gross_sales || 0) - (a.agg.gross_sales || 0));
    return arr;
  }, [rows, groupBy, sortMode, statusFilter]);

  const statusCounts = useMemo(() => {
    const c = { ahead: 0, ontarget: 0, behind: 0 };
    rows.length && Object.values(rows.reduce((m, r) => { const k = r[groupBy] || "—"; (m[k] = m[k] || []).push(r); return m; }, {}))
      .forEach(rs => { const s = cibleStatus(aggregate(rs)); if (s) c[s] += 1; });
    return c;
  }, [rows, groupBy]);

  const totalAgg = useMemo(() => aggregate(rows), [rows]);

  const columns = grouped.slice(0, Math.min(visibleCount, grouped.length));
  const hidden = Math.max(0, grouped.length - visibleCount);

  // Line structure with block colors
  const BLOCKS = [
    { key: "ventes", label: "1. VENTES", color: "bg-blue-50", accent: "border-l-4 border-blue-500", text: "text-blue-900", isHeader: true },
    { key: "sales_ttc",       label: "Sales TTC",             kind: "eur", indent: true,   fromAgg: a => a.sales_ttc },
    { key: "tva",             label: "TVA",                   kind: "eur", indent: true,   fromAgg: a => a.tva },
    { key: "gross_sales",     label: "Sales HT",              kind: "eur", indent: true,   fromAgg: a => a.gross_sales, bold: true },
    { key: "taux_commissions",label: "Commissions (%)",       kind: "pct", indent: true,   fromAgg: a => a.taux_commissions },
    { key: "sales_ht_net_com",label: "Sales HT net de com.",  kind: "eur", indent: true,   fromAgg: a => a.sales_ht_net_com, bold: true },

    { key: "achats", label: "2. ACHATS & MARCHANDISES", color: "bg-emerald-50", accent: "border-l-4 border-emerald-500", text: "text-emerald-900", isHeader: true },
    { key: "food_costs",      label: "Foodcost (€)",          kind: "eur", indent: true,   fromAgg: a => a.food_costs },
    { key: "ratio_foodcost",  label: "Foodcost (%)",          kind: "pct", indent: true,   fromAgg: a => a.ratio_foodcost, bold: true, cible: "cible_ratio_achat", lowerIsBetter: true },
    { key: "marge_brute",     label: "Marge brute (€)",       kind: "eur", indent: true,   fromAgg: a => a.marge_brute },
    { key: "taux_mb",         label: "Taux MB (%)",           kind: "pct", indent: true,   fromAgg: a => a.taux_mb, cible: "cible_taux_mb" },

    { key: "ms", label: "3. MASSE SALARIALE", color: "bg-rose-50", accent: "border-l-4 border-rose-500", text: "text-rose-900", isHeader: true },
    { key: "labor_costs",     label: "Masse salariale (€)",   kind: "eur", indent: true,   fromAgg: a => a.labor_costs },
    { key: "taux_ms",         label: "Taux MS (%)",           kind: "pct", indent: true,   fromAgg: a => a.taux_ms, bold: true, cible: "cible_taux_ms", lowerIsBetter: true },
    { key: "productivite",    label: "Productivité (€/h)",    kind: "eurh", indent: true,  fromAgg: a => a.productivite, cible: "cible_prod" },

    { key: "other", label: "4. AUTRES CHARGES", color: "bg-violet-50", accent: "border-l-4 border-violet-500", text: "text-violet-900", isHeader: true },
    { key: "other_costs",     label: "Autres charges (€)",    kind: "eur", indent: true,   fromAgg: a => a.other_costs },
    { key: "taux_other",      label: "Taux Autres (%)",       kind: "pct", indent: true,   fromAgg: a => a.taux_other, cible: "cible_taux_other", lowerIsBetter: true },

    { key: "res", label: "5. RÉSULTATS", color: "bg-amber-50", accent: "border-l-4 border-amber-500", text: "text-amber-900", isHeader: true },
    { key: "contribution",    label: "Résultat d'exploitation (EBE)", kind: "eur", indent: true, fromAgg: a => a.contribution, bold: true },
    { key: "taux_mn",         label: "Marge nette (%)",       kind: "pct", indent: true,   fromAgg: a => a.taux_mn, bold: true, cible: "cible_taux_mn" },
    { key: "contrib_ebe",     label: "Contribution / % MN (EBE)", kind: "pct", indent: true, fromAgg: a => a.taux_mn },
  ];

  const renderVal = (line, agg) => {
    if (line.isHeader) return null;
    const raw = line.fromAgg(agg);
    if (line.kind === "eurh") return agg.heures ? fmtNum(raw, 1) + " €/h" : "—";
    if (unit === "pct" && line.kind === "eur" && agg.gross_sales) {
      return fmtPct(raw / agg.gross_sales);
    }
    if (unit === "eur" && line.kind === "pct") return fmtPct(raw);
    return line.kind === "eur" ? fmtEur(raw) : fmtPct(raw);
  };
  // Écart vs cible (pt pour les taux, €/h pour la productivité) — vert si favorable, rouge sinon, gris dans ±0,1
  const renderGap = (line, agg) => {
    if (!showCibles || !line.cible) return null;
    const target = agg[line.cible]; const val = line.fromAgg(agg);
    if (target == null || val == null || isNaN(val) || (line.kind === "eurh" && !agg.heures) || (line.kind === "pct" && !agg.gross_sales)) return null;
    const diff = line.kind === "pct" ? (val - target) * 100 : val - target;
    const favorable = line.lowerIsBetter ? diff <= 0 : diff >= 0;
    const neutral = Math.abs(diff) < 0.1;
    const txt = `${diff > 0 ? "+" : ""}${diff.toFixed(1).replace(".", ",")}${line.kind === "pct" ? " pt" : " €/h"}`;
    return <div className={`text-[9px] font-semibold leading-tight ${neutral ? "text-slate-400" : favorable ? "text-emerald-600" : "text-rose-600"}`} title={`Cible ${line.kind === "pct" ? fmtPct(target) : fmtNum(target, 1) + " €/h"}`} data-testid={`wp-gap-${line.key}`}>{txt} vs cible</div>;
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-[0_1px_3px_rgba(15,23,42,0.04)]" data-testid="wp-detailed-grid">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-base font-bold text-slate-900">Compte de résultat détaillé · {monthLabel}</h2>
          <p className="text-[11px] text-slate-500">Montants en € · Variation en % vs N-1{canCompare && <span className="text-blue-600 font-semibold"> · Cliquez une colonne pour comparer {years[0]} vs {years[1]}</span>}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Group toggle */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="wp-group-toggle">
            {[
              { k: "corner",   l: "Par corner" },
              { k: "secteur",  l: "Par secteur" },
              { k: "dr",       l: "Par DR" },
              { k: "enseigne", l: "Par enseigne" },
            ].map(t => (
              <button key={t.k} onClick={() => setGroupBy(t.k)}
                      data-testid={`wp-group-${t.k}`}
                      className={`text-[11px] px-2.5 py-1 rounded-md font-semibold ${groupBy === t.k ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>
                {t.l}
              </button>
            ))}
          </div>
          {/* Unit toggle */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
            <button onClick={() => setUnit("eur")} data-testid="wp-unit-eur"
                    className={`text-[11px] px-2.5 py-1 rounded-md font-semibold ${unit === "eur" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>€</button>
            <button onClick={() => setUnit("pct")} data-testid="wp-unit-pct"
                    className={`text-[11px] px-2.5 py-1 rounded-md font-semibold ${unit === "pct" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>% du CA</button>
          </div>
          <button onClick={() => setShowCibles(v => !v)} data-testid="wp-toggle-gaps" title="Afficher l'écart de chaque indicateur par rapport à sa cible"
                  className={`text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border ${showCibles ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-500 hover:text-slate-800"}`}>
            Écarts vs cible
          </button>
          <button onClick={onOpenCibles} data-testid="wp-open-cibles"
                  className="flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-800">
            <Target className="w-3.5 h-3.5"/> Cibles
          </button>
          <select value={sortMode} onChange={e => setSortMode(e.target.value)} data-testid="wp-sort-mode"
                  className="text-[11px] font-semibold px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700">
            {SORT_MODES.map(([k, l]) => <option key={k} value={k}>Tri : {l}</option>)}
          </select>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="wp-status-filter">
            {[["all", `Tous`], ["ahead", `En avance (${statusCounts.ahead})`], ["ontarget", `Objectif (${statusCounts.ontarget})`], ["behind", `En retard (${statusCounts.behind})`]].map(([k, l]) => (
              <button key={k} onClick={() => { setStatusFilter(k); setVisibleCount(10); }} data-testid={`wp-status-${k}`}
                      className={`text-[11px] px-2.5 py-1 rounded-md font-semibold ${statusFilter === k ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
            ))}
          </div>
          <div className="flex items-center gap-1 text-[11px] text-slate-500">
            <LayoutGrid className="w-3.5 h-3.5"/>
            <span>{columns.length}/{grouped.length}</span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto" style={{ maxHeight: 560 }}>
        <table className="text-[11px] border-collapse w-full">
          <thead className="sticky top-0 z-20 bg-white">
            <tr className="border-b-2 border-slate-200">
              <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-slate-500 text-[10px] sticky left-0 z-30 bg-white border-r border-slate-200 min-w-[240px]">
                Ligne du compte de résultat
              </th>
              {columns.map(g => (
                <th key={g.key} className={`px-2 py-2 text-right font-bold text-slate-900 min-w-[110px] border-r border-slate-100 whitespace-nowrap ${canCompare ? "cursor-pointer hover:bg-blue-50 group" : ""}`}
                    onClick={() => canCompare && setCmpGroup(g)} title={canCompare ? `Comparer ${years[0]} vs ${years[1]} — ${g.label}` : undefined} data-testid={`wp-col-${g.key}`}>
                  <div className="text-[11px] truncate max-w-[130px] group-hover:text-blue-700">{g.label}{canCompare && <span className="ml-1 text-[9px] text-blue-500 opacity-0 group-hover:opacity-100">⇄</span>}</div>
                  <div className="text-[9px] font-normal text-slate-500 truncate max-w-[130px]">{g.secteur || g.dr || g.enseigne || "—"}</div>
                  {g.status && <div className={`mt-0.5 inline-block text-[8px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${CIBLE_STATUS[g.status].cls}`} data-testid={`wp-col-status-${g.key}`} title={`MN ${fmtPct(g.agg.taux_mn)} vs cible ${fmtPct(g.agg.cible_taux_mn)}`}>{CIBLE_STATUS[g.status].label}</div>}
                </th>
              ))}
              <th className="px-2 py-2 text-right font-bold text-slate-900 min-w-[110px] bg-slate-100 border-l-2 border-slate-300 sticky right-0 z-30 whitespace-nowrap">
                <div className="text-[11px]">TOTAL RÉSEAU</div>
                <div className="text-[9px] font-normal text-slate-500">{grouped.length} entités</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {BLOCKS.map(line => {
              if (line.isHeader) {
                return (
                  <tr key={line.key} className={`${line.color}`} data-testid={`wp-block-${line.key}`}>
                    <td className={`px-3 py-1.5 font-bold uppercase tracking-wider text-[11px] ${line.text} ${line.accent} sticky left-0 z-10 ${line.color}`}>
                      {line.label}
                    </td>
                    {columns.map(g => <td key={g.key} className={`${line.color}`}/>)}
                    <td className={`${line.color} border-l-2 border-slate-300 sticky right-0 z-10`}/>
                  </tr>
                );
              }
              return (
                <tr key={line.key} className="border-b border-slate-100 hover:bg-slate-50/70">
                  <td className={`px-3 py-1 sticky left-0 z-10 bg-white border-r border-slate-100 ${line.bold ? "font-bold text-slate-900" : "text-slate-700"} ${line.indent ? "pl-6" : ""}`}>
                    {line.label}
                  </td>
                  {columns.map(g => (
                    <td key={g.key} className={`px-2 py-1 text-right tabular-nums border-r border-slate-100 ${line.bold ? "font-bold text-slate-900" : "text-slate-700"}`}>
                      {renderVal(line, g.agg)}{renderGap(line, g.agg)}
                    </td>
                  ))}
                  <td className={`px-2 py-1 text-right tabular-nums bg-slate-50 border-l-2 border-slate-300 sticky right-0 z-10 ${line.bold ? "font-bold text-slate-900" : "font-semibold text-slate-800"}`}>
                    {renderVal(line, totalAgg)}{renderGap(line, totalAgg)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {hidden > 0 && (
        <div className="p-3 border-t border-slate-100 flex items-center justify-between bg-slate-50">
          <span className="text-[11px] text-slate-600">
            {hidden} {groupBy === "corner" ? "corner" : groupBy}{hidden > 1 ? "s" : ""} masqué{hidden > 1 ? "s" : ""}
          </span>
          <button onClick={() => setVisibleCount(v => v + 10)} data-testid="wp-show-more"
                  className="text-[11px] font-semibold text-blue-600 hover:underline">
            Afficher {Math.min(10, hidden)} de plus →
          </button>
        </div>
      )}
      {cmpGroup && (
        <div className="fixed inset-0 z-[1200] bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4" onClick={() => setCmpGroup(null)} data-testid="wp-yc-modal">
          <div className="w-full max-w-5xl max-h-[92vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="bg-[#0b1f4d] text-white rounded-t-xl px-4 py-3 flex items-center justify-between">
              <div><div className="text-[10px] uppercase tracking-wider opacity-70">{groupBy === "corner" ? "Corner" : groupBy === "dr" ? "DR" : groupBy === "secteur" ? "Secteur" : "Enseigne"}</div><div className="font-display font-bold">{cmpGroup.label}</div></div>
              <button onClick={() => setCmpGroup(null)} data-testid="wp-yc-close" className="w-8 h-8 grid place-items-center rounded-lg hover:bg-white/10"><XIcon className="w-4 h-4"/></button>
            </div>
            <div className="[&>div]:rounded-t-none"><YearComparison rows={cmpGroup.rows} years={years}/></div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Vue Synthèse widgets ───────────────────────────────────────────── */
function EbeMonthlyChart({ monthlyAgg, currentMonth }) {
  const data = monthlyAgg.map(m => ({
    mois: MONTH_ABBR[m.mois] || m.mois,
    "Réalisé CA": Math.round(m.gross_sales),
    "Objectif CA": Math.round(m.obj_ca_ht || m.gross_sales * 1.03),
    "EBE": Math.round(m.contribution),
  }));
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)]" data-testid="wp-ebe-chart">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-slate-800 text-sm">Évolution mensuelle du résultat d'exploitation (EBE)</h3>
      </div>
      <div className="h-72">
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
            <XAxis dataKey="mois" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false}/>
            <YAxis tickFormatter={fmtEurShort} tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} width={55}/>
            <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 12 }} formatter={fmtEurShort}/>
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="line"/>
            <Bar dataKey="EBE" fill="#DBEAFE" radius={[4, 4, 0, 0]}/>
            <Line type="monotone" dataKey="Objectif CA" stroke={CLR.orange} strokeDasharray="5 3" strokeWidth={2} dot={false}/>
            <Line type="monotone" dataKey="Réalisé CA" stroke={CLR.blue} strokeWidth={2.5} dot={{ r: 3, fill: CLR.blue }} activeDot={{ r: 5 }}/>
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* Première dimension renseignée dans les lignes importées (aucune jointure externe) */
const dimWithData = (rows, dims) => dims.find(d => rows.some(r => (r[d] || "").toString().trim())) || null;
const DIM_LABEL = { enseigne: "enseigne", region: "région", secteur: "secteur", dr: "DR", corner: "corner" };

const REP_METRICS = [["gross_sales", "CA HT", r => r.gross_sales || 0], ["contribution", "Marge nette", r => r.contribution || 0], ["labor_costs", "Masse salariale", r => Math.abs(r.labor_costs || 0)], ["food_costs", "Foodcost", r => Math.abs(r.food_costs || 0)], ["marge_brute", "Marge brute", r => (r.gross_sales || 0) - Math.abs(r.food_costs || 0)]];
function EnseigneDonut({ rows }) {
  const [metric, setMetric] = useState("gross_sales"); const [dimSel, setDimSel] = useState("auto");
  const dimAuto = useMemo(() => dimWithData(rows, ["enseigne", "secteur", "dr"]), [rows]);
  const dim = dimSel === "auto" ? dimAuto : dimSel; const M = REP_METRICS.find(m => m[0] === metric) || REP_METRICS[0];
  const data = useMemo(() => {
    const groups = {};
    if (!dim) return [];
    rows.forEach(r => {
      const k = dimKey(r, dim);
      if (/^autres?$/i.test(k) || /CHARGES COMMUNES/i.test(k)) return;
      groups[k] = (groups[k] || 0) + M[2](r);
    });
    return Object.entries(groups).map(([name, value]) => ({ name, value })).filter(x => x.value > 0).sort((a, b) => b.value - a.value);
  }, [rows, dim, M]);
  const total = data.reduce((s, x) => s + x.value, 0);
  const totalAll = rows.reduce((s, r) => s + Math.max(0, M[2](r) || 0), 0);
  const excluded = totalAll - total;
  const nonRenseigne = data.find(x => x.name === NON_RENSEIGNE)?.value || 0;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)]" data-testid="wp-enseigne-donut">
      <div className="flex items-start justify-between gap-2 mb-2 flex-wrap"><h3 className="font-bold text-slate-800 text-sm">Répartition {M[1]} {dim && <span className="text-slate-400 font-normal">par {DIM_LABEL[dim]}</span>}</h3>
        <div className="flex items-center gap-1 flex-wrap">
          <div className="flex gap-0.5 bg-slate-100 rounded-md p-0.5" data-testid="wp-rep-metric">{REP_METRICS.map(([k, l]) => <button key={k} onClick={() => setMetric(k)} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${metric === k ? "bg-white text-blue-700 shadow-sm" : "text-slate-500"}`} data-testid={`wp-rep-metric-${k}`}>{l}</button>)}</div>
          <select value={dimSel} onChange={e => setDimSel(e.target.value)} className="text-[10px] border border-slate-200 rounded px-1 py-0.5" data-testid="wp-rep-dim"><option value="auto">Auto</option><option value="enseigne">Enseigne</option><option value="region">Région</option><option value="dr">DR</option><option value="secteur">Secteur</option></select>
        </div></div>
      {dim && dim !== "enseigne" && <p className="text-[10px] text-amber-700 mb-2" data-testid="wp-donut-fallback">Colonne « Enseigne » absente du fichier importé pour cette période — répartition par {DIM_LABEL[dim]}.</p>}
      {dim && nonRenseigne > 0 && <p className="text-[10px] text-slate-500 mb-2" data-testid="wp-donut-nonrenseigne">« {NON_RENSEIGNE} » = {fmtEur(nonRenseigne)} : lignes du fichier sans colonne {DIM_LABEL[dim]?.toLowerCase() || dim} (ex. mois importés depuis un fichier sans cette colonne). Même périmètre que les cards et le graphique.</p>}
      {dim && excluded > Math.max(1, totalAll * 0.005) && <p className="text-[10px] text-slate-500 mb-2" data-testid="wp-donut-excluded">Hors {fmtEur(excluded)} (lignes « Autres » / charges communes).</p>}
      <div className="flex items-center gap-4">
        <div className="h-52 w-52 relative flex-shrink-0">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius={55} outerRadius={80} paddingAngle={2}>
                {data.map((e, i) => <Cell key={i} fill={ENSEIGNE_COLORS[e.name] || `hsl(${i * 47}, 70%, 55%)`}/>)}
              </Pie>
              <Tooltip formatter={v => fmtEurShort(v)}/>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="text-center">
              <div className="text-[9px] uppercase font-bold text-slate-500">{M[1]}</div>
              <div className="text-[13px] font-bold text-slate-900 tabular-nums">{fmtEurShort(total)}</div>
            </div>
          </div>
        </div>
        <div className="flex-1 space-y-1.5">
          {data.map(d => (
            <div key={d.name} className="flex items-center gap-2 text-[11px]">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: ENSEIGNE_COLORS[d.name] || "#94A3B8" }}/>
              <span className="text-slate-700 font-medium flex-1 truncate">{d.name}</span>
              <span className="text-slate-500 tabular-nums">{fmtEurShort(d.value)}</span><span className="text-slate-900 font-bold tabular-nums w-10 text-right">{total ? Math.round(d.value / total * 100) : 0}%</span>
            </div>
          ))}
          {data.length === 0 && <div className="text-xs italic text-slate-400">Aucune donnée</div>}
        </div>
      </div>
    </div>
  );
}

function PerformanceRegion({ rows, drilldownCorners }) {
  const dim = useMemo(() => dimWithData(rows, ["region", "secteur", "dr"]), [rows]);
  // Agg per region → marge nette %
  const data = useMemo(() => {
    const map = {};
    if (!dim) return [];
    rows.forEach(r => {
      const k = dimKey(r, dim);
      if (!map[k]) map[k] = { region: k, gross_sales: 0, contribution: 0, prev_contribution: 0, prev_gross_sales: 0 };
      map[k].gross_sales += r.gross_sales || 0;
      map[k].contribution += r.contribution || 0;
      map[k].prev_gross_sales += r.sales_n1 || 0;
    });
    return Object.values(map)
      .filter(r => !/CHARGES COMMUNES/i.test(r.region))
      .map(r => ({
        ...r,
        mn: r.gross_sales ? r.contribution / r.gross_sales : 0,
        delta: r.prev_gross_sales ? (r.gross_sales - r.prev_gross_sales) / r.prev_gross_sales : null,  // évolution CA vs N-1 (fichier)
      }))
      .sort((a, b) => b.mn - a.mn);
  }, [rows, dim]);
  const maxMn = Math.max(...data.map(d => d.mn), 0.15);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)]" data-testid="wp-perf-region">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-slate-800 text-sm">Performance par {DIM_LABEL[dim] || "région"} <span className="text-slate-400 font-normal">(marge nette)</span></h3>
      </div>
      {dim && dim !== "region" && <p className="text-[10px] text-amber-700 mb-2" data-testid="wp-region-fallback">Colonne « Region » absente du fichier importé pour cette période — lecture par {DIM_LABEL[dim]}.</p>}
      <div className="space-y-2">
        {data.map(r => {
          const w = maxMn ? (r.mn / maxMn) * 100 : 0;
          const good = r.mn >= 0.08;
          return (
            <div key={r.region} className="grid grid-cols-[100px_60px_1fr] items-center gap-3 text-[11px]">
              <span className="text-slate-700 font-medium truncate">{r.region}</span>
              <span className="text-slate-900 font-bold tabular-nums">{fmtPct(r.mn)}</span>
              <div className="h-4 rounded bg-slate-100 overflow-hidden relative">
                <div className="h-full rounded transition-all" style={{ width: `${w}%`, background: good ? CLR.green : CLR.orange }}/>
              </div>
            </div>
          );
        })}
        {data.length === 0 && <div className="text-xs italic text-slate-400 py-4 text-center">Aucune région</div>}
      </div>
    </div>
  );
}

function TopWatchCorners({ rows, kind }) {
  const isTop = kind === "top";
  const data = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      const k = r.corner; if (!k) return;
      if (!map[k]) map[k] = { corner: k, gross_sales: 0, contribution: 0 };
      map[k].gross_sales += r.gross_sales || 0;
      map[k].contribution += r.contribution || 0;
    });
    const arr = Object.values(map)
      .filter(c => c.gross_sales > 0)
      .map(c => ({ ...c, mn: c.contribution / c.gross_sales }));
    if (isTop) return arr.sort((a, b) => b.mn - a.mn).slice(0, 5);
    return arr.sort((a, b) => a.mn - b.mn).slice(0, 5);
  }, [rows, isTop]);
  return (
    <div className={`bg-white border rounded-xl p-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)] ${isTop ? "border-amber-200" : "border-rose-200"}`}
         data-testid={isTop ? "wp-top-corners" : "wp-watch-corners"}>
      <div className="flex items-center gap-2 mb-3">
        {isTop ? <Trophy className="w-4 h-4 text-amber-500"/> : <AlertTriangle className="w-4 h-4 text-rose-500"/>}
        <h3 className="font-bold text-slate-800 text-sm">{isTop ? "Top 5 corners" : "Corners à surveiller"}</h3>
        <span className="text-[10px] text-slate-400 italic">(marge nette)</span>
      </div>
      <div className="space-y-1.5">
        {data.map((c, i) => (
          <div key={c.corner} className="flex items-center gap-2 text-[11px] py-1">
            <span className={`w-5 h-5 rounded-full grid place-items-center font-bold text-[10px] shrink-0 ${isTop ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`}>{i + 1}</span>
            <span className="flex-1 truncate font-semibold text-slate-800">{c.corner}</span>
            <span className={`font-bold tabular-nums ${isTop ? "text-emerald-700" : "text-rose-700"}`}>{fmtPct(c.mn)}</span>
            <span className="text-slate-500 font-medium tabular-nums w-20 text-right">{fmtEurShort(c.gross_sales)}</span>
          </div>
        ))}
        {data.length === 0 && <div className="text-xs italic text-slate-400 py-2 text-center">Aucun corner</div>}
      </div>
    </div>
  );
}

function IndicateursReseau({ rows, monthlyAgg }) {
  const agg = useMemo(() => aggregate(rows), [rows]);
  const corners = new Set(rows.map(r => r.corner).filter(Boolean));
  const daysInMonth = 30;
  const caPerCorner = corners.size ? agg.gross_sales / corners.size : 0;
  const caPerDay = corners.size ? agg.gross_sales / corners.size / daysInMonth : 0;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)]" data-testid="wp-indic-reseau">
      <h3 className="font-bold text-slate-800 text-sm mb-3">Indicateurs clés réseau</h3>
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center">
          <div className="text-[9px] font-bold uppercase text-slate-500 mb-1">Nb de corners</div>
          <div className="text-[20px] font-bold text-slate-900 tabular-nums">{corners.size}</div>
          <div className="text-[10px] text-slate-500 mt-1">Actifs</div>
        </div>
        <div className="text-center border-x border-slate-200">
          <div className="text-[9px] font-bold uppercase text-slate-500 mb-1">CA moyen / jour</div>
          <div className="text-[20px] font-bold text-slate-900 tabular-nums">{fmtEurShort(caPerDay)}</div>
          <div className="text-[10px] text-slate-500 mt-1">par corner</div>
        </div>
        <div className="text-center">
          <div className="text-[9px] font-bold uppercase text-slate-500 mb-1">Productivité</div>
          <div className="text-[20px] font-bold text-slate-900 tabular-nums">{fmtNum(agg.productivite, 1)} €</div>
          <div className="text-[10px] text-slate-500 mt-1">par heure</div>
        </div>
      </div>
    </div>
  );
}

function HeatmapRegion({ rows }) {
  const LEVELS = [["region", "Région"], ["secteur", "Secteur"], ["corner", "Corner"]];
  const available = useMemo(() => LEVELS.map(([k]) => k).filter(k => rows.some(r => (r[k] || "").toString().trim())), [rows]);
  const [levelSel, setLevel] = useState("region"); // region | secteur | corner
  const [sort, setSort] = useState({ k: "avg", d: -1 }); // name | avg | <mois>
  const level = available.includes(levelSel) ? levelSel : (available[0] || "corner");
  const data = useMemo(() => {
    const groups = {};
    rows.forEach(r => {
      const key = dimKey(r, level);
      if (/CHARGES COMMUNES/i.test(key)) return;
      const mois = r.mois || "";
      groups[key] = groups[key] || {};
      groups[key][mois] = groups[key][mois] || { gross_sales: 0, contribution: 0 };
      groups[key][mois].gross_sales += r.gross_sales || 0;
      groups[key][mois].contribution += r.contribution || 0;
    });
    // Tous les mois de la période sélectionnée (12 mois → 12 colonnes, 3 mois → 3 colonnes)
    const months = [...new Set(rows.map(r => r.mois).filter(Boolean))].sort((a, b) => MONTHS_FR.indexOf(a) - MONTHS_FR.indexOf(b));
    const mn = (b, m) => b[m] ? (b[m].gross_sales ? b[m].contribution / b[m].gross_sales : null) : null;
    const regions = Object.entries(groups).map(([name, byMois]) => { const mns = months.map(m => mn(byMois, m)); const v = mns.filter(x => x != null); return { name, byMois, mns, avg: v.length ? v.reduce((s, x) => s + x, 0) / v.length : null }; });
    regions.sort((a, b) => { if (sort.k === "name") return a.name.localeCompare(b.name) * sort.d; const x = sort.k === "avg" ? a.avg : a.mns[months.indexOf(sort.k)]; const y = sort.k === "avg" ? b.avg : b.mns[months.indexOf(sort.k)]; if (x == null) return 1; if (y == null) return -1; return (x - y) * sort.d; });
    // Ligne total : taux réseau pondéré (Σ contribution / Σ CA) par mois, et sur la période
    const totals = months.map(m => { let gs = 0, c = 0; Object.values(groups).forEach(b => { if (b[m]) { gs += b[m].gross_sales; c += b[m].contribution; } }); return gs ? c / gs : null; });
    let GS = 0, C = 0; Object.values(groups).forEach(b => Object.values(b).forEach(v => { GS += v.gross_sales; C += v.contribution; }));
    return { regions, months, totals, totalAvg: GS ? C / GS : null };
  }, [rows, level, sort]);
  const Th = ({ k, label, cls = "" }) => <th className={`px-2 py-1 font-bold text-slate-500 select-none ${cls} ${sort.k === k ? "text-blue-700" : ""}`} data-testid={`wp-heatmap-th-${k}`}><span className="inline-flex items-center gap-0.5">{label}<span className="inline-flex flex-col leading-none ml-0.5"><button onClick={() => setSort({ k, d: -1 })} title="Du plus grand au plus petit" className={`text-[8px] leading-[8px] ${sort.k === k && sort.d < 0 ? "text-blue-700" : "text-slate-300 hover:text-slate-500"}`} data-testid={`wp-heatmap-sort-desc-${k}`}>▲</button><button onClick={() => setSort({ k, d: 1 })} title="Du plus petit au plus grand" className={`text-[8px] leading-[8px] ${sort.k === k && sort.d > 0 ? "text-blue-700" : "text-slate-300 hover:text-slate-500"}`} data-testid={`wp-heatmap-sort-asc-${k}`}>▼</button></span></span></th>;
  const scoreColor = mn => {
    if (mn == null || isNaN(mn)) return "bg-slate-100";
    if (mn >= 0.25) return "bg-emerald-600 text-white";
    if (mn >= 0.22) return "bg-emerald-300 text-emerald-900";
    if (mn >= 0.20) return "bg-orange-400 text-white";
    return "bg-rose-500 text-white";
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)]" data-testid="wp-heatmap-region">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <h3 className="font-bold text-slate-800 text-sm">Heatmap performance par {LEVELS.find(l => l[0] === level)[1].toLowerCase()}</h3>
          <p className="text-[10px] text-slate-500">Taux EBE / marge nette (%) — {data.months.length} mois de la période sélectionnée · {data.regions.length} {level === "corner" ? "corners" : level === "secteur" ? "secteurs" : "régions"} · tri : {sort.k === "name" ? "nom" : sort.k === "avg" ? "moyenne" : MONTH_ABBR[sort.k] || sort.k} {sort.d < 0 ? "décroissant" : "croissant"}</p>
        </div>
        <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5" data-testid="wp-heatmap-level">
          {LEVELS.map(([k, l]) => { const ok = available.includes(k); return <button key={k} disabled={!ok} onClick={() => setLevel(k)} data-testid={`wp-heatmap-level-${k}`} title={ok ? undefined : `Colonne « ${l} » absente du fichier importé`} className={`text-[11px] px-3 py-1 rounded-md font-semibold ${level === k ? "bg-blue-600 text-white shadow-sm" : ok ? "text-slate-600 hover:text-slate-900" : "text-slate-300 cursor-not-allowed line-through"}`}>{l}</button>; })}
        </div>
      </div>
      <div className={`overflow-x-auto ${level === "corner" ? "max-h-[420px] overflow-y-auto" : ""}`}>
        <table className="w-full text-[10px] border-collapse">
          <thead className="sticky top-0 bg-white">
            <tr>
              <Th k="name" label="" cls="text-left" />
              {data.months.map(m => <Th key={m} k={m} label={MONTH_ABBR[m] || m} cls="text-center" />)}
              <Th k="avg" label="Moy." cls="text-center border-l border-slate-200" />
            </tr>
          </thead>
          <tbody>
            {data.regions.map(({ name: reg, mns, avg }) => {
              return (
                <tr key={reg} className="border-t border-slate-100" data-testid={`wp-heatmap-row-${reg}`}>
                  <td className="px-2 py-1 text-left font-semibold text-slate-700 whitespace-nowrap">{reg}</td>
                  {mns.map((mn, i) => (
                    <td key={i} className="px-1 py-1 text-center">
                      <div className={`rounded px-1.5 py-1 font-semibold tabular-nums text-[10px] ${scoreColor(mn)}`}>{mn != null ? fmtPct(mn, 1) : "—"}</div>
                    </td>
                  ))}
                  <td className="px-2 py-1 text-center border-l border-slate-200"><div className={`rounded px-1.5 py-1 font-bold tabular-nums ${scoreColor(avg)}`}>{avg != null ? fmtPct(avg, 1) : "—"}</div></td>
                </tr>
              );
            })}
            {data.regions.length === 0 && <tr><td colSpan={data.months.length + 2} className="text-center py-4 text-slate-400 italic">Aucune donnée</td></tr>}
          </tbody>
          {data.regions.length > 0 && <tfoot className="sticky bottom-0 bg-slate-50"><tr className="border-t-2 border-slate-300" data-testid="wp-heatmap-total">
            <td className="px-2 py-1 text-left font-bold text-slate-800 whitespace-nowrap">Moyenne / total</td>
            {data.months.map((m, i) => { const t = data.totals[i]; return <td key={m} className="px-1 py-1 text-center"><div className={`rounded px-1.5 py-1 font-bold tabular-nums text-[10px] ${scoreColor(t)}`}>{t != null ? fmtPct(t, 1) : "—"}</div></td>; })}
            <td className="px-2 py-1 text-center border-l border-slate-200"><div className={`rounded px-1.5 py-1 font-bold tabular-nums ${scoreColor(data.totalAvg)}`}>{data.totalAvg != null ? fmtPct(data.totalAvg, 1) : "—"}</div></td>
          </tr></tfoot>}
        </table>
      </div>
      <div className="mt-3 flex items-center gap-3 text-[9px] text-slate-500 flex-wrap" data-testid="wp-heatmap-legend">
        <div className="flex items-center gap-1"><span className="w-3 h-3 bg-rose-500 rounded"/> &lt; 20 %</div>
        <div className="flex items-center gap-1"><span className="w-3 h-3 bg-orange-400 rounded"/> 20 – 22 %</div>
        <div className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-300 rounded"/> 22 – 25 %</div>
        <div className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-600 rounded"/> &gt; 25 %</div>
      </div>
    </div>
  );
}

function InsightsIA({ kpisCur, kpisPrev, rows }) {
  const insights = useMemo(() => {
    const lines = [];
    if (!kpisCur || !kpisPrev) return lines;
    // CA delta
    const caDelta = kpisPrev.gross_sales ? ((kpisCur.gross_sales - kpisPrev.gross_sales) / kpisPrev.gross_sales) * 100 : 0;
    if (caDelta > 0) lines.push({ icon: "up", color: "text-emerald-600", text: `Le CA est en hausse de ${caDelta.toFixed(1).replace(".", ",")}% par rapport au mois précédent.` });
    else if (caDelta < 0) lines.push({ icon: "down", color: "text-rose-600", text: `Le CA est en baisse de ${Math.abs(caDelta).toFixed(1).replace(".", ",")}% par rapport au mois précédent.` });
    // Foodcost delta
    const fcDelta = (kpisCur.ratio_foodcost - kpisPrev.ratio_foodcost) * 100;
    if (fcDelta < -0.3) lines.push({ icon: "info", color: "text-blue-600", text: `Le foodcost baisse de ${Math.abs(fcDelta).toFixed(1).replace(".", ",")} pt à ${(kpisCur.ratio_foodcost * 100).toFixed(1).replace(".", ",")}%. Amélioration nette.` });
    else if (fcDelta > 0.3) lines.push({ icon: "warn", color: "text-amber-600", text: `Le foodcost augmente de +${fcDelta.toFixed(1).replace(".", ",")} pt à ${(kpisCur.ratio_foodcost * 100).toFixed(1).replace(".", ",")}%. Vigilance recommandée.` });
    // Corners under target
    const underMn = rows.filter(r => r.gross_sales && (r.contribution / r.gross_sales) < 0.05).map(r => r.corner);
    if (underMn.length > 0) lines.push({ icon: "warn", color: "text-amber-600", text: `${underMn.length} corners sont sous l'objectif de marge nette (5%). ${underMn.slice(0, 3).join(", ")}${underMn.length > 3 ? "…" : ""} nécessitent un plan d'action.` });
    // EBE delta
    const ebeDelta = kpisPrev.contribution ? ((kpisCur.contribution - kpisPrev.contribution) / kpisPrev.contribution) * 100 : 0;
    if (Math.abs(ebeDelta) > 3) lines.push({
      icon: ebeDelta > 0 ? "up" : "down", color: ebeDelta > 0 ? "text-violet-600" : "text-rose-600",
      text: `L'EBE ${ebeDelta > 0 ? "progresse" : "recule"} de ${ebeDelta > 0 ? "+" : ""}${ebeDelta.toFixed(1).replace(".", ",")}%. ${ebeDelta > 0 ? "Très bonne performance opérationnelle sur le réseau." : "Attention à la rentabilité opérationnelle."}`,
    });
    return lines.slice(0, 4);
  }, [kpisCur, kpisPrev, rows]);

  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-100 p-4 shadow-[0_1px_3px_rgba(15,23,42,.04)]" data-testid="wp-insights-ia">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <OrbitAI className="w-4 h-4 text-purple-600"/>
          <h3 className="font-bold text-slate-800 text-sm">Insights IA — Synthèse du mois</h3>
        </div>
      </div>
      <div className="space-y-2 text-[12px] text-slate-700">
        {insights.length === 0 && <div className="italic text-slate-400 text-center py-2">Aucun insight — données insuffisantes.</div>}
        {insights.map((line, i) => (
          <div key={i} className="flex items-start gap-2 bg-white/60 rounded-lg p-2">
            <span className={`text-lg leading-none mt-0.5 ${line.color}`}>
              {line.icon === "up" ? "↗" : line.icon === "down" ? "↘" : line.icon === "warn" ? "⚠" : "ℹ"}
            </span>
            <span>{line.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────────────── */
export default function KpiBusinessV2() {
  const [filters, setFilters] = useState(null);
  const [f, setF] = useSharedKpiFilters({ drs: [], secteurs: [], corners: [], exercice: null, mois: [], annees: [] }, { meta: filters, pageKey: "orbit_wp_filters" });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(() => new URLSearchParams(window.location.search).get("view") || "synthese"); // synthese | detaillee | simulateur | ia | rapports
  const nav = useNavigate();
  const wpLoc = useLocation();
  useEffect(() => { const v = new URLSearchParams(wpLoc.search).get("view"); if (v) setView(v); }, [wpLoc.search]);
  const TABS = [["synthese", LayoutDashboard, "Vue synthèse"], ["detaillee", Table2, "Vue détaillée"], ["simulateur", Calculator, "Simulateur EBE"], ["ia", OrbitAI, "Analyse IA"], ["rapports", FileText, "Rapports"]];
  const [showCibles, setShowCibles] = useState(false);
  const { user } = useAuth();
  const canEditCibles = ["admin", "controller"].includes(user?.role);
  const [manualCibles, setManualCibles] = useState({ targets: {}, corners: {} });
  const [ciblesVersion, setCiblesVersion] = useState(0);
  useEffect(() => {
    api.get("/kpi/business/cibles").then(r => { MANUAL_TARGETS = { targets: r.data.targets || {}, corners: r.data.corners || {} }; setManualCibles(MANUAL_TARGETS); setCiblesVersion(v => v + 1); }).catch(() => {});
  }, []);
  const saveCibles = async (targets, cornerKey) => {
    const { data } = await api.put("/kpi/business/cibles", { targets, corner_key: cornerKey });
    MANUAL_TARGETS = { targets: data.targets || {}, corners: data.corners || {} }; setManualCibles(MANUAL_TARGETS); setCiblesVersion(v => v + 1);
  };

  useEffect(() => {
    api.get("/kpi/business/meta").then(r => {
      const d = r.data || {};
      // Adapte au shape attendu par UnifiedFilters (drs / bms / secteurs / corners …)
      setFilters({
        drs: d.drs || [], bms: d.bms || [], secteurs: d.secteurs || [],
        enseignes: d.enseignes || [], regions: d.regions || [],
        corners: d.corners || [],
        annees: d.annees || [], mois: d.mois || [], exercices: d.exercices || [],
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true; setLoading(true);
    const params = toParams({ drs: f.drs, secteurs: f.secteurs, corners: f.corners, annees: f.annees });
    if (f.exercice) params.exercice = f.exercice;
    if (f.date_from) params.date_from = f.date_from;
    if (f.date_to) params.date_to = f.date_to;
    api.get("/kpi/business/rows", { params })
      .then(r => { if (alive) setRows((r.data || []).map(x => ({ ...x, mois: normMois(x.mois), secteur: (x.secteur && x.secteur !== "0" ? x.secteur : null) || x.bm || null }))); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [f.drs, f.secteurs, f.corners, f.exercice, f.annees, f.date_from, f.date_to]);

  // Filter rows client-side by mois
  const filteredRows = useMemo(() => {
    if (!f.mois?.length) return rows;
    return rows.filter(r => f.mois.includes(r.mois));
  }, [rows, f.mois]);

  const exercicesFromData = useMemo(() => [...new Set(rows.map(r => r.exercice).filter(Boolean))].sort(), [rows]);

  // Monthly aggregation for chart + KPIs vs prev month
  const monthlyAgg = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      const key = r.mois; if (!key) return;
      map[key] = map[key] || { mois: key, rows: [] };
      map[key].rows.push(r);
    });
    return Object.entries(map)
      .map(([mois, v]) => ({ mois, ...aggregate(v.rows), order: MONTHS_FR.indexOf(mois) }))
      .sort((a, b) => a.order - b.order);
  }, [rows]);

  // Période personnalisée : comparaison vs période précédente de même durée
  const [prevRows, setPrevRows] = useState(null);
  const pp = prevPeriod(f);
  const ppKey = JSON.stringify(pp?.params || null);
  useEffect(() => {
    if (!pp || !isMultiMonth(f)) { setPrevRows(null); return; }
    let alive = true;
    const params = { ...toParams({ drs: f.drs, secteurs: f.secteurs, corners: f.corners }), ...pp.params };
    api.get("/kpi/business/rows", { params })
      .then(r => { if (alive) setPrevRows((r.data || []).map(x => ({ ...x, mois: normMois(x.mois) }))); })
      .catch(() => { if (alive) setPrevRows(null); });
    return () => { alive = false; };
  }, [f.drs, f.secteurs, f.corners, ppKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // KPI cards : 1 mois sélectionné (ou aucun) → mois + variation vs mois précédent ; plusieurs mois ou dates personnalisées → cumul de la période filtrée (= tableau détaillé)
  const { kpisCur, kpisPrev, monthLabel, deltaLabel: cardDeltaLabel } = useMemo(() => {
    const monthsInData = monthlyAgg.map(m => m.mois);
    const customRange = !!(f.date_from && f.date_to);
    if (isMultiMonth(f)) {
      // Année / exercice / plusieurs mois / dates personnalisées → cumul de la période filtrée (= tableaux), comparé à la période précédente équivalente
      const cur = filteredRows.length ? { ...aggregate(filteredRows), mois: "PERIODE", rows: filteredRows } : null;
      const nbMois = new Set(filteredRows.map(r => `${r.annee}-${r.mois}`)).size;
      const years = [...new Set(filteredRows.map(r => r.annee).filter(Boolean))].sort();
      const fmtD = (s) => s ? s.split("-").reverse().join("/") : "";
      const label = customRange ? `${nbMois} mois · du ${fmtD(f.date_from)} au ${fmtD(f.date_to)}`
        : f.exercice && !(f.mois || []).length ? `Exercice ${f.exercice} · ${nbMois} mois`
        : `${nbMois} mois${years.length ? ` · ${years.join("–")}` : ""}`;
      const prev = prevRows?.length ? { ...aggregate(prevRows), mois: "PERIODE_PREC", rows: prevRows } : null;
      return { kpisCur: cur, kpisPrev: prev, monthLabel: label, deltaLabel: pp?.label || "vs période préc." };
    }
    const target = f.mois?.[0] || monthsInData[monthsInData.length - 1];
    const idx = monthsInData.indexOf(target);
    const cur = idx >= 0 ? monthlyAgg[idx] : (monthlyAgg[monthlyAgg.length - 1] || null);
    const prev = idx > 0 ? monthlyAgg[idx - 1] : (monthlyAgg[monthlyAgg.length - 2] || null);
    const label = cur ? `${cur.mois.charAt(0) + cur.mois.slice(1).toLowerCase()} ${cur.rows?.[0]?.annee || ""}` : "—";
    return { kpisCur: cur, kpisPrev: prev, monthLabel: label, deltaLabel: "vs mois préc." };
  }, [monthlyAgg, f.mois, filteredRows, f.date_from, f.date_to, prevRows, ciblesVersion, f.exercice, f.annees]); // eslint-disable-line react-hooks/exhaustive-deps

  const kpiCards = useMemo(() => {
    if (!kpisCur) return null;
    const p = kpisPrev || {};
    const dPct = (a, b) => (a != null && b != null) ? (a - b) * 100 : null;
    return [
      { key: "ca",  icon: Database,     iconColor: "text-blue-600",    iconBg: "bg-blue-50",
        label: "CA HT total", value: fmtEur(kpisCur.gross_sales),
        delta: p.gross_sales ? ((kpisCur.gross_sales - p.gross_sales) / p.gross_sales) * 100 : null, unit: "%" },
      { key: "mb",  icon: Percent,      iconColor: "text-teal-600",    iconBg: "bg-teal-50",
        label: "Marge brute", value: fmtPct(kpisCur.taux_mb),
        delta: dPct(kpisCur.taux_mb, p.taux_mb), unit: "pt" },
      { key: "fc",  icon: ShoppingCart, iconColor: "text-emerald-600", iconBg: "bg-emerald-50",
        label: "Foodcost", value: fmtPct(kpisCur.ratio_foodcost),
        delta: dPct(kpisCur.ratio_foodcost, p.ratio_foodcost), unit: "pt" },
      { key: "ms",  icon: Users,        iconColor: "text-amber-600",   iconBg: "bg-amber-50",
        label: "Masse salariale", value: fmtPct(kpisCur.taux_ms),
        delta: dPct(kpisCur.taux_ms, p.taux_ms), unit: "pt" },
      { key: "ebe", icon: Wallet,       iconColor: "text-violet-600",  iconBg: "bg-violet-50",
        label: "EBE total", value: fmtEur(kpisCur.contribution),
        delta: p.contribution ? ((kpisCur.contribution - p.contribution) / p.contribution) * 100 : null, unit: "%" },
      { key: "mn",  icon: TrendingUp,   iconColor: "text-rose-600",    iconBg: "bg-rose-50",
        label: "Marge nette (MN)", value: fmtPct(kpisCur.taux_mn),
        delta: dPct(kpisCur.taux_mn, p.taux_mn), unit: "pt" },
    ];
  }, [kpisCur, kpisPrev]);

  const onExport = () => {
    if (!filteredRows.length) { toast.error("Aucune ligne à exporter"); return; }
    const cols = ["corner","region","dr","secteur","bm","enseigne","mois","annee","exercice","sales_ttc","gross_sales","food_costs","marge_brute","labor_costs","contribution","taux_mb","taux_ms","taux_mn","ratio_achat"];
    const csv = ["\ufeff" + cols.map(c => `"${c}"`).join(";")];
    filteredRows.forEach(r => csv.push(cols.map(c => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(";")));
    const url = URL.createObjectURL(new Blob([csv.join("\r\n")], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = `wasaplan_${monthLabel.replace(/ /g, "_")}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success(`Export réussi — ${filteredRows.length} lignes`);
  };

  return (
    <div className="space-y-4" data-testid="kpi-business-v2-page">
      {/* ─── Header ─── */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-600 text-white grid place-items-center font-bold">📊</div>
            <div>
              <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 uppercase leading-none">WASAPLAN</h1>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {view === "synthese" ? "Vue synthèse — Performance par niveau (Réseau / Région / DR / BM)" : view === "detaillee" ? "Compte de résultat mensuel Anaplan · Corner par corner" : view === "simulateur" ? "Simulateur de résultat d'exploitation (EBE) — projection non destructive du compte de résultat" : view === "ia" ? "Analyse IA — lecture automatique des indicateurs du périmètre" : "Rapports — génération de rapports WASAPLAN"}
              </p>
              <LastImportInfo kinds={["wasaplan", "pl"]} className="mt-1.5"/><CreateReportButton module="ca_daily" className="mt-2 ml-2"/>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PageActions title="WASAPLAN" filters={() => document.querySelector("[data-testid=wp-filters]")?.innerText?.replace(/\s+/g, " ").slice(0, 160) || ""} onAi={() => setView("ia")} onExcel={onExport} testid="wp-actions" />
        </div>
      </div>

      {/* ─── Onglets (à gauche, au-dessus des filtres) ─── */}
      <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5 w-fit" data-testid="wp-view-toggle">
        {TABS.map(([k, I, l]) => <button key={k} onClick={() => setView(k)} data-testid={`wp-view-${k}`}
                className={`inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-md font-semibold ${view === k ? "bg-blue-600 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
          <I className="w-3.5 h-3.5"/>{l}
        </button>)}
      </div>

      {/* ─── Filters ─── */}
      <FilterBar filters={filters} f={f} setF={setF} exercicesFromData={exercicesFromData}/>

      {/* ─── 6 KPI Cards ─── */}
      {(view === "synthese" || view === "detaillee" || view === "ia") && <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2" data-testid="wp-kpi-row">
        {kpiCards ? kpiCards.map(c => (
          <KpiCard key={c.key} icon={c.icon} iconColor={c.iconColor} iconBg={c.iconBg} deltaLabel={cardDeltaLabel}
                   label={c.label} value={c.value} delta={c.delta} unit={c.unit}
                   testid={`wp-kpi-${c.key}`}/>
        )) : (
          [...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 px-3 py-3 h-[92px] animate-pulse"/>
          ))
        )}
      </div>}

      {/* ─── VIEW: SYNTHESE ─── */}
      {view === "synthese" && (
        <div className="space-y-4" data-testid="wp-view-synthese-content">
          {(f.annees || []).length === 2 && <YearComparison rows={filteredRows} years={[...f.annees].sort((a, b) => a - b)}/>}
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-4">
            <EbeMonthlyChart monthlyAgg={monthlyAgg} currentMonth={kpisCur}/>
            <EnseigneDonut rows={filteredRows}/>
          </div>
          <HeatmapRegion rows={filteredRows}/>
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)] gap-4">
            <CommissionsTable rows={filteredRows}/>
            <RatioEvolution rows={filteredRows} field="labor_costs" label="Masse salariale" color="#3B82F6" testid="wp-evol-ms"/>
            <RatioEvolution rows={filteredRows} field="food_costs" label="Foodcost" color="#F59E0B" testid="wp-evol-fc"/>
          </div>
          <InsightsIA kpisCur={kpisCur} kpisPrev={kpisPrev} rows={filteredRows}/>
        </div>
      )}

      {/* ─── VIEW: DETAILLEE ─── */}
      {view === "detaillee" && (
        <div className="space-y-4" data-testid="wp-view-detaillee-content">
          <DetailedGrid key={`grid-${ciblesVersion}`} rows={filteredRows} monthLabel={monthLabel} onOpenCibles={() => setShowCibles(true)} years={(f.annees || []).length === 2 ? [...f.annees].sort((a, b) => a - b) : null}/>
        </div>
      )}

      {showCibles && <CiblesPanel rows={filteredRows} manual={manualCibles} onSaveTargets={saveCibles} canEdit={canEditCibles} onClose={() => setShowCibles(false)}/>}

      {view === "simulateur" && <EbeSimulator f={f} onOpenDetail={() => setView("detaillee")}/>}

      {view === "ia" && <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="wp-view-ia-content"><InsightsIA kpisCur={kpisCur} kpisPrev={kpisPrev} rows={filteredRows}/><IndicateursReseau rows={filteredRows} monthlyAgg={monthlyAgg}/></div>}

      {view === "rapports" && <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-3" data-testid="wp-view-rapports-content">
        <h3 className="text-[14px] font-bold text-slate-900">Rapports WASAPLAN</h3>
        <p className="text-[12px] text-slate-500">Générez un vrai compte de résultat (Ventes → Achats → Masse salariale → Autres charges → EBE) en PDF, PowerPoint ou Web via le module « Compte de résultat » du Report Studio, ou exportez les lignes filtrées en Excel.</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => nav("/reports?new=pnl")} data-testid="wp-rapports-studio" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-xs font-bold"><FileText className="w-4 h-4"/> Ouvrir le Report Studio</button>
          <button onClick={onExport} data-testid="wp-rapports-export" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-xs font-bold text-slate-700"><Download className="w-4 h-4"/> Exporter Excel ({filteredRows.length} lignes)</button>
        </div>
      </div>}
      {loading && <div className="fixed bottom-4 right-4 bg-slate-900 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg animate-pulse">Chargement…</div>}
    </div>
  );
}
